import { Box, ButtonBase, Paper, Skeleton, Stack, Typography } from '@mui/material';
import { useAppState } from '../../context/LoadTestContext';
import { useUnits } from '../units';
import { isUnloadStep, stepLabel } from '../steps';
import ShowUnloadStepsSwitch from '../ShowUnloadStepsSwitch';

// "MM:SS" (minutes can pass 60) -> minutes.
export function durationMinutes(display) {
  const [m, s] = String(display || '').split(':').map(Number);
  return Number.isFinite(m) ? m + (Number.isFinite(s) ? s / 60 : 0) : null;
}

// One card per step from step_summary: hold time, load, and the step's
// settlement (from the end of the previous step). Load steps (S1, S2, ...) are
// shown; the switch adds the unload steps (U1, U2, ...). Picking one marks it
// on the Load vs Deflection graph and drives the readings column's rate.
// selectedStep and onSelect use the step's number in step_summary.
function StepStrip({ selectedStep, onSelect }) {
  const { loading, stepSummaryData, showUnloadSteps } = useAppState();
  const units = useUnits();
  const steps = Array.isArray(stepSummaryData) ? stepSummaryData : [];
  const loadCount = steps.filter((s) => !isUnloadStep(s)).length;

  return (
    <Paper component='section' aria-label='Load steps' sx={{ p: 1.5, borderRadius: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Stack direction='row' alignItems='baseline' flexWrap='wrap' columnGap={1.5} rowGap={0.5}>
        <Typography variant='subtitle1' component='h2' fontWeight={600}>
          {showUnloadSteps ? `Steps (${loadCount} load, ${steps.length - loadCount} unload)` : `Load steps (${steps.length})`}
        </Typography>
        <Typography variant='caption' color='text.secondary'>Pick a step to mark it on the graph</Typography>
        <Stack direction='row' alignItems='center' flexWrap='wrap' columnGap={1.5} sx={{ ml: 'auto' }}>
          <ShowUnloadStepsSwitch />
        </Stack>
      </Stack>
      <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 0.5 }}>
        {loading && [0, 1, 2, 3, 4].map((i) => (
          <Skeleton key={i} variant='rectangular' animation='wave' sx={{ flex: '0 0 auto', width: 112, height: 78, borderRadius: 1 }} />
        ))}
        {!loading && steps.length === 0 && (
          <Typography variant='body2' color='text.secondary' sx={{ py: 2 }}>{showUnloadSteps ? 'No steps yet.' : 'No load steps yet.'}</Typography>
        )}
        {!loading && steps.map((s) => {
          const selected = s.step === selectedStep;
          const unload = isUnloadStep(s);
          return (
            <ButtonBase
              key={s.step}
              aria-pressed={selected}
              aria-label={`${unload ? 'Unload' : 'Load'} step ${stepLabel(s)}`}
              onClick={() => onSelect(s.step)}
              sx={(theme) => ({
                flex: '0 0 auto',
                minWidth: 112,
                minHeight: 78,
                px: 1.25,
                py: 1,
                borderRadius: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: 0.25,
                textAlign: 'left',
                border: 1,
                borderStyle: unload && !selected ? 'dashed' : 'solid',
                borderColor: selected ? 'transparent' : 'divider',
                // Unload steps sit lighter than load steps, on the card's own background.
                backgroundColor: selected
                  ? (theme.palette.mode === 'dark' ? theme.palette.primary.main : theme.palette.secondary.main)
                  : (unload ? 'background.paper' : 'background.dashboard'),
                color: selected
                  ? (theme.palette.mode === 'dark' ? '#0a0a0a' : '#ffffff')
                  : 'text.primary',
              })}
            >
              <Stack direction='row' alignItems='center' spacing={0.75} sx={{ width: '100%' }}>
                <Typography variant='caption' sx={{ opacity: 0.85 }}>{stepLabel(s)} · {s.step_duration_display}</Typography>
                {unload && (
                  <Box
                    component='span'
                    sx={{
                      ml: 'auto',
                      px: 0.5,
                      borderRadius: 0.5,
                      border: 1,
                      borderColor: selected ? 'currentColor' : 'warning.main',
                      color: selected ? 'inherit' : 'warning.main',
                      fontSize: 10,
                      fontWeight: 600,
                      lineHeight: '14px',
                    }}
                  >
                    Unload
                  </Box>
                )}
              </Stack>
              <Typography sx={{ fontSize: 16, fontWeight: 600 }}>
                {s.load === null || s.load === undefined ? '—' : `${units.fmtForce(s.load)} ${units.forceUnit}`}
              </Typography>
              <Typography variant='caption'>Δ {units.fmtLength(s.settlement)} {units.lengthUnit}</Typography>
            </ButtonBase>
          );
        })}
      </Box>
    </Paper>
  );
}

export default StepStrip;
